import type {
  DetrixActionLine,
  DetrixBrowserResourceEntry,
  DetrixEvent,
  DetrixNetworkEntry,
  DetrixSession,
  DetrixUserStep,
} from '../types';
import type { TabId } from '../store/tabId';
import { formatSessionOffset } from './time';

export type IssueCounts = { errors: number; warnings: number };

export type FirstErrorTarget = { tab: TabId; id: string; time: number };

export type ErrorCorrelation = {
  prevActionId: string | null;
  relatedNetworkId: string | null;
  errorTime: number;
};

function isEventError(ev: DetrixEvent): boolean {
  return ev.type === 'error' || ev.type === 'system.error';
}

function isNetworkFail(status: number, err?: unknown): boolean {
  if (err) return true;
  return status >= 400;
}

function isNetworkWarn(status: number, err?: unknown): boolean {
  if (err) return false;
  return status >= 400 && status < 500;
}

export function countSessionIssues(session: DetrixSession): IssueCounts {
  let errors = 0;
  let warnings = 0;

  for (const s of session.steps ?? []) {
    if (s.kind === 'error') errors++;
  }
  for (const a of session.actions ?? []) {
    if (a.kind === 'error') errors++;
  }
  for (const n of session.network ?? []) {
    const st = n.response?.status ?? 0;
    if (isNetworkFail(st, n.error)) errors++;
    else if (isNetworkWarn(st, n.error)) warnings++;
  }
  for (const r of session.browserResources ?? []) {
    if (Boolean(r.error) || r.status >= 400) errors++;
    else if (r.status >= 300 && r.status < 400) warnings++;
  }
  for (const e of session.events ?? []) {
    if (isEventError(e)) errors++;
  }

  return { errors, warnings };
}

function pushErrorCandidate(
  out: { time: number; tab: TabId; id: string }[],
  time: number,
  tab: TabId,
  id: string,
) {
  if (Number.isFinite(time)) out.push({ time, tab, id });
}

/** Earliest strict error (not warnings-only) in the session. */
export function findFirstErrorInSession(session: DetrixSession): FirstErrorTarget | null {
  const c: { time: number; tab: TabId; id: string }[] = [];

  for (const s of session.steps ?? []) {
    if (s.kind === 'error') pushErrorCandidate(c, s.timestampStart, 'steps', s.id);
  }
  for (const a of session.actions ?? []) {
    if (a.kind === 'error') pushErrorCandidate(c, a.timestamp, 'actions', a.id);
  }
  for (const n of session.network ?? []) {
    const st = n.response?.status ?? 0;
    if (isNetworkFail(st, n.error)) pushErrorCandidate(c, n.timestamp, 'network', n.id);
  }
  for (const r of session.browserResources ?? []) {
    if (Boolean(r.error) || r.status >= 400) pushErrorCandidate(c, r.timestamp, 'network', r.id);
  }
  for (const e of session.events ?? []) {
    if (isEventError(e)) pushErrorCandidate(c, e.timestamp, 'events', e.id);
  }

  if (c.length === 0) return null;
  c.sort((a, b) => a.time - b.time || a.id.localeCompare(b.id));
  const first = c[0]!;
  return { tab: first.tab, id: first.id, time: first.time };
}

export type SessionErrorListItem = {
  time: number;
  tab: TabId;
  id: string;
  /** One-line description for roster UI */
  summary: string;
};

function truncateLabel(s: string, max: number): string {
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1)}…`;
}

function shortUrlLabel(u: string, max = 44): string {
  return truncateLabel(u.replace(/^https?:\/\//, ''), max);
}

/** Every strict error in session time order (for header roster / navigation). */
export function listSessionErrors(session: DetrixSession): SessionErrorListItem[] {
  const items: SessionErrorListItem[] = [];

  for (const s of session.steps ?? []) {
    if (s.kind !== 'error') continue;
    items.push({
      time: s.timestampStart,
      tab: 'steps',
      id: s.id,
      summary: truncateLabel(s.title || 'Step error', 72),
    });
  }
  for (const a of session.actions ?? []) {
    if (a.kind !== 'error') continue;
    items.push({
      time: a.timestamp,
      tab: 'actions',
      id: a.id,
      summary: truncateLabel(a.summary || 'Action error', 72),
    });
  }
  for (const n of session.network ?? []) {
    const st = n.response?.status ?? 0;
    if (!isNetworkFail(st, n.error)) continue;
    items.push({
      time: n.timestamp,
      tab: 'network',
      id: n.id,
      summary: `${n.request.method} ${shortUrlLabel(n.request.url, 40)} → ${st || 'ERR'}`,
    });
  }
  for (const r of session.browserResources ?? []) {
    if (!r.error && r.status < 400) continue;
    items.push({
      time: r.timestamp,
      tab: 'network',
      id: r.id,
      summary: `${r.resourceType} ${shortUrlLabel(r.url, 36)} · ${r.status}`,
    });
  }
  for (const e of session.events ?? []) {
    if (!isEventError(e)) continue;
    items.push({
      time: e.timestamp,
      tab: 'events',
      id: e.id,
      summary: truncateLabel(e.type, 56),
    });
  }

  items.sort((a, b) => a.time - b.time || a.id.localeCompare(b.id));
  return items;
}

function stepById(session: DetrixSession, id: string): DetrixUserStep | undefined {
  return session.steps?.find((s) => s.id === id);
}

function actionById(session: DetrixSession, id: string): DetrixActionLine | undefined {
  return session.actions?.find((a) => a.id === id);
}

function networkById(session: DetrixSession, id: string): DetrixNetworkEntry | undefined {
  return session.network.find((n) => n.id === id);
}

function resourceById(session: DetrixSession, id: string): DetrixBrowserResourceEntry | undefined {
  return session.browserResources?.find((r) => r.id === id);
}

function eventById(session: DetrixSession, id: string): DetrixEvent | undefined {
  return session.events.find((e) => e.id === id);
}

export function isSelectionError(session: DetrixSession, tab: TabId, selectedId: string | null): boolean {
  if (!selectedId) return false;
  switch (tab) {
    case 'steps': {
      const s = stepById(session, selectedId);
      return Boolean(s && s.kind === 'error');
    }
    case 'actions': {
      const a = actionById(session, selectedId);
      return Boolean(a && a.kind === 'error');
    }
    case 'network': {
      const n = networkById(session, selectedId);
      if (n) {
        const st = n.response?.status ?? 0;
        return isNetworkFail(st, n.error);
      }
      const r = resourceById(session, selectedId);
      return Boolean(r && (Boolean(r.error) || r.status >= 400));
    }
    case 'events': {
      const e = eventById(session, selectedId);
      return Boolean(e && isEventError(e));
    }
    default:
      return false;
  }
}

function errorTimestampForSelection(session: DetrixSession, tab: TabId, selectedId: string): number | null {
  switch (tab) {
    case 'steps': {
      const s = stepById(session, selectedId);
      return s ? s.timestampStart : null;
    }
    case 'actions': {
      const a = actionById(session, selectedId);
      return a ? a.timestamp : null;
    }
    case 'network': {
      const n = networkById(session, selectedId);
      if (n) return n.timestamp;
      const r = resourceById(session, selectedId);
      return r ? r.timestamp : null;
    }
    case 'events': {
      const e = eventById(session, selectedId);
      return e ? e.timestamp : null;
    }
    default:
      return null;
  }
}

/** Last user action at or before `t`, and the strongest failing network at or before `t` (prefer latest failed request). */
export function getErrorCorrelation(
  session: DetrixSession,
  tab: TabId,
  selectedId: string | null,
): ErrorCorrelation | null {
  if (!selectedId || !isSelectionError(session, tab, selectedId)) return null;
  const t = errorTimestampForSelection(session, tab, selectedId);
  if (t == null) return null;

  const actions = [...(session.actions ?? [])].sort((a, b) => a.timestamp - b.timestamp);
  let prevAction: DetrixActionLine | null = null;
  for (const a of actions) {
    if (a.timestamp <= t) prevAction = a;
    else break;
  }

  const nets = [...(session.network ?? [])].sort((a, b) => a.timestamp - b.timestamp || a.sequence - b.sequence);
  let related: DetrixNetworkEntry | null = null;
  for (const n of nets) {
    if (n.timestamp > t + 2) break;
    const st = n.response?.status ?? 0;
    if (isNetworkFail(st, n.error)) related = n;
  }

  return {
    prevActionId: prevAction?.id ?? null,
    relatedNetworkId: related?.id ?? null,
    errorTime: t,
  };
}

function truncate(s: string, max: number): string {
  if (s.length <= max) return s;
  return `${s.slice(0, max - 1)}…`;
}

export function buildErrorNarrativeLines(session: DetrixSession, tab: TabId, selectedId: string | null): string[] {
  if (!selectedId || !isSelectionError(session, tab, selectedId)) return [];

  const selectionT = errorTimestampForSelection(session, tab, selectedId);
  const corr = getErrorCorrelation(session, tab, selectedId);
  const whenMs = selectionT ?? corr?.errorTime ?? 0;

  const lines: string[] = ['What happened'];

  if (corr) {
    if (corr.prevActionId) {
      const a = actionById(session, corr.prevActionId);
      if (a) lines.push(`User: ${truncate(a.summary, 120)}`);
    }

    if (corr.relatedNetworkId) {
      const n = networkById(session, corr.relatedNetworkId);
      if (n) {
        const st = n.response?.status ?? 0;
        const msg = n.error?.message ?? n.response?.statusText ?? '';
        lines.push(
          `${n.request.method} ${truncate(n.request.url, 80)} → ${st || 'ERR'}${msg ? ` (${truncate(msg, 80)})` : ''}`,
        );
      }
    }
  }

  switch (tab) {
    case 'steps': {
      const s = stepById(session, selectedId);
      if (s) lines.push(`Step failed: ${truncate(s.title, 100)}`);
      break;
    }
    case 'actions': {
      const a = actionById(session, selectedId);
      if (a) lines.push(`Action error: ${truncate(a.summary, 120)}`);
      break;
    }
    case 'network': {
      const n = networkById(session, selectedId);
      if (n) {
        const st = n.response?.status ?? 0;
        lines.push(`Request failed (${st || 'ERR'}) at ${formatSessionOffset(whenMs)}`);
        if (n.error?.message) lines.push(truncate(n.error.message, 200));
      } else {
        const r = resourceById(session, selectedId);
        if (r) lines.push(`Resource failed (${r.status}) at ${formatSessionOffset(whenMs)}`);
      }
      break;
    }
    case 'events': {
      const e = eventById(session, selectedId);
      if (e) {
        lines.push(`Event: ${e.type} at ${formatSessionOffset(whenMs)}`);
        const payload = e.payload as Record<string, unknown> | null;
        const msg =
          (typeof payload?.message === 'string' && payload.message) ||
          (typeof payload?.error === 'string' && payload.error) ||
          '';
        if (msg) lines.push(truncate(msg, 200));
      }
      break;
    }
  }

  return lines;
}
